import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { SalesReportResponse } from "@shared/reports-sales";

const PRIMARY: [number, number, number] = [194, 101, 42];
const MUTED: [number, number, number] = [120, 120, 120];
const DARK: [number, number, number] = [30, 30, 30];

function usd(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function longDate(iso: string): string {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

type SectionCursor = { y: number };

function addSection(doc: jsPDF, cursor: SectionCursor, title: string): void {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (cursor.y > pageHeight - 40) {
    doc.addPage();
    cursor.y = 16;
  }
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...DARK);
  doc.text(title, 14, cursor.y);
  cursor.y += 3;
}

function addTable(doc: jsPDF, cursor: SectionCursor, head: string[], body: (string | number)[][], foot?: (string | number)[]): void {
  autoTable(doc, {
    startY: cursor.y,
    head: [head],
    body,
    foot: foot ? [foot] : undefined,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2, textColor: DARK },
    headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: "bold" },
    footStyles: { fillColor: [252, 247, 242], textColor: DARK, fontStyle: "bold" },
    columnStyles: Object.fromEntries(head.map((h, i) => [i, /Gross|Paid|Pending|Total|Outstanding|Amount/.test(h) ? { halign: "right" } : {}])),
    margin: { left: 14, right: 14 },
  });
  const finalY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? cursor.y;
  cursor.y = finalY + 10;
}

/**
 * One clean "Gross Sales Report" PDF: header + period, summary, sales by client,
 * sales by type, invoices issued. Detail lines are left to the CSV export.
 */
export function exportSalesPdf(report: SalesReportResponse, orgName: string): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const { summary, period } = report;

  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 26, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Gross Sales Report", 14, 12);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(orgName, 14, 19);
  doc.text(`${longDate(period.start)} – ${longDate(period.end)}`, pageWidth - 14, 12, { align: "right" });
  doc.text(`Generated ${new Date().toLocaleDateString("en-US")}`, pageWidth - 14, 19, { align: "right" });

  const cursor: SectionCursor = { y: 36 };

  addSection(doc, cursor, "Summary");
  addTable(doc, cursor, ["Metric", "Amount"], [
    ["Gross sales", usd(summary.grossSales)],
    ["Paid", usd(summary.paid)],
    ["Pending", usd(summary.pending)],
    ["Line items", summary.lineItems],
    ["Cleaning jobs billed", summary.jobs],
    ["Clients billed", summary.clients],
    ["Apartments serviced", summary.apartments],
    ["Invoices issued in period", `${summary.invoicedCount} · ${usd(summary.invoicedTotal)}`],
    ["Collected on those invoices", usd(summary.collected)],
    ["Outstanding on those invoices", usd(summary.outstanding)],
  ]);

  addSection(doc, cursor, "Sales by client");
  addTable(
    doc,
    cursor,
    ["Client", "Apartments", "Jobs", "Gross", "Paid", "Pending"],
    report.byClient.map(c => [c.clientName, c.apartments, c.jobs, usd(c.gross), usd(c.paid), usd(c.pending)]),
    ["Total", summary.apartments, summary.jobs, usd(summary.grossSales), usd(summary.paid), usd(summary.pending)],
  );

  addSection(doc, cursor, "Sales by type");
  addTable(
    doc,
    cursor,
    ["Type", "Line items", "Gross"],
    report.byType.map(t => [t.label, t.lineItems, usd(t.gross)]),
    ["Total", summary.lineItems, usd(summary.grossSales)],
  );

  if (report.invoices && report.invoices.length > 0) {
    addSection(doc, cursor, "Invoices issued in period");
    addTable(
      doc,
      cursor,
      ["Invoice", "Client", "Issued", "Service period", "Status", "Total", "Paid", "Outstanding"],
      report.invoices.map(i => [
        i.invoiceNumber, i.clientName, longDate(i.issuedOn), `${longDate(i.periodStart)} – ${longDate(i.periodEnd)}`,
        i.status, usd(i.total), usd(i.paid), usd(i.outstanding),
      ]),
      ["Total", "", "", "", "", usd(summary.invoicedTotal), usd(summary.collected), usd(summary.outstanding)],
    );
  }

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(`Period basis: service date in ${period.timezone}. Page ${p} of ${pages}`, 14, doc.internal.pageSize.getHeight() - 6);
  }

  doc.save(`gross-sales-${period.start}-to-${period.end}.pdf`);
}
